/**
 * handler-row-mobile.js
 */

import React from 'react';
import { Link } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Link as RouterLink } from 'react-router-dom';
import { Types } from 'utils/constants';
import { UserContext } from 'contexts/user/user-context';
import RowMobile from './row-mobile';
import BarChart from './bar-chart';
import {
  NameContent,
  Menu,
  handlerLateRecordsPath,
  handlerTrainingPath,
  handlerDeploymentPath,
} from './handler-shared';


const useStyles = makeStyles(theme => ({
  mobileDetail: {
    display: 'flex',
    flexDirection: 'column',    
    '& .label': {
      width: 60,
      maxWidth: 60,
      marginRight: theme.spacing(1),
      fontSize: theme.typography.sizes[1],
      fontWeight: 500,
      lineHeight: '16px',
      whiteSpace: 'nowrap',
    },
    '& .stats': {
      display: 'flex',
      '& .stats-list': {
        display: 'flex',
        columnGap: '6vw',
      },
      '& .stat': {
        display: 'flex',
      },
      '& .value': {
        fontSize: theme.typography.sizes[5],
        fontWeight: 500,
        paddingRight: 6,
      },
      '& .stat-label': {
        fontSize: theme.typography.sizes[0],
        lineHeight: '12px'
      }
    },
    '& .charts': {
      marginTop: theme.spacing(1),
      '& .chart-container': {
        padding: `${theme.spacing(1)} 0`
      },
      '& .chart': {
        height: 35,
        width: `calc(100% - 80px)`,
        maxWidth: 300,
        '& > *': {
          padding: 0
        },
        '& canvas': {
          width: '100% !important',
          height: '100% !important'
        }
      }
    }
  },
}));


const MobileDetail = (props) => {
  const { stats, charts } = props;
  const classes = useStyles();
  return (
    <div className={classes.mobileDetail}>
      <div className='stats'>
        <div className='label'>Last 3<br />Months</div>
        <div className='stats-list'>
          {stats.map((stat, i) => <MobileStat key={i} {...stat} />)}
        </div>
      </div>
      <div className='charts'>
        {charts.map((chart, i) => <MobileChart key={i} {...chart} />)}
      </div>
    </div>
  )
}


const MobileStat = (props) => {
  const { value, label, path } = props;
  return (
    <Link
      component={RouterLink}
      to={path}
      children={(
        <div className='stat flex-hc'>
          <div className='value flex-hc'>{value}</div>
          <div className='stat-label flex-hc'>{label}</div>
        </div>
      )}
      underline="hover" />
  );
}


const MobileChart = (props) => {
  const { label, chart } = props;
  return (
    <div className='chart-container flex-hc'>
      <div className='label flex-hc'>{label}</div>
      <div className='chart flex-hc'>{chart}</div>
    </div>
  )
}


export default function HandlerRowMobile(props) {
  const {
    member,
    onRemove,
    isExpanded
  } = props;
  const { user } = React.useContext(UserContext);

  const stats = React.useMemo(() => {
    if (user.role.isSupervisor) {
      return [{
        value: member.recentLateRecordsCount,
        label: <>Late<br />Record{member.recentLateRecordsCount === 1 ? '' : 's'}</>,
        path: handlerLateRecordsPath(member.id)
      }, {
        value: member.recentTrainingHoursCount,
        label: <>Training<br />Hour{member.recentTrainingHoursCount === 1 ? '' : 's'}</>,
        path: handlerTrainingPath(member.id)
      }, {
        value: member.recentDeploymentsCount,
        label: <>Total<br />Deploy{member.recentDeploymentsCount === 1 ? '' : 's'}</>,
        path: handlerDeploymentPath(member.id)
      }];
    } else if (user.role.isTrainer) {
      return [{
        value: member.recentLateRecordsCount,
        label: <>Late<br />Record{member.recentLateRecordsCount === 1 ? '' : 's'}</>,
        path: handlerLateRecordsPath(member.id)
      }, {
        value: member.recentEventsAttendedCount,
        label: <>Event{member.recentEventsAttendedCount === 1 ? '' : 's'}<br />Attended</>,
        path: handlerTrainingPath(member.id)
      }, {
        value: member.recentTrainingHoursCount,
        label: <>Training<br />Hour{member.recentTrainingHoursCount === 1 ? '' : 's'}</>,
        path: handlerTrainingPath(member.id)
      }];
    } else {
      return [];
    }
  }, [user, member]);

  const charts = React.useMemo(() => {
    if (user.role.isSupervisor) {
      return [{
        label: <>Training<br />By Month</>,
        chart: <BarChart recordGroup={Types.RecordGroup.TRAINING} data={member.trainingStats} />
      }, {
        label: <>Deploys<br />By Month</>,
        chart: <BarChart recordGroup={Types.RecordGroup.DEPLOYMENT} data={member.deploymentStats} />
      }];
    } else if (user.role.isTrainer) {
      return [{
        label: <>Training<br />By Month</>,
        chart: <BarChart recordGroup={Types.RecordGroup.TRAINING} data={member.trainingStats} />
      }];
    } else {
      return [];
    }
  }, [user, member]);


  return (
    <>
      <RowMobile
        nameContent={<NameContent member={member} />}
        menu={<Menu member={member} onRemove={onRemove} />}
        expansionContent={<MobileDetail stats={stats} charts={charts} />}
        isExpanded={isExpanded}
      />
    </>
  );
}